import { z } from "zod";

// A Ref points at the result of an earlier op in the same batch. `path` walks
// into the result object/array. Refs are the model's way to thread data
// between ops without an extra postMessage roundtrip per dependency.
export const RefSchema: z.ZodType<{
  ref: number;
  path?: (string | number)[];
}> = z.object({
  ref: z.number().int().nonnegative(),
  path: z.array(z.union([z.string(), z.number().int().nonnegative()])).optional(),
});
export type Ref = z.infer<typeof RefSchema>;

const slideRef = z.union([z.number().int().nonnegative(), RefSchema]);

export const OpSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("getOutline") }),
  z.object({ op: z.literal("addSlide") }),
  z.object({ op: z.literal("getSelection") }),
  z.object({
    op: z.literal("setFirstShapeText"),
    slide: slideRef,
    text: z.string(),
  }),
  // Used by insert_deck_slides / insert_sandbox_deck_slides. Inserts slides
  // from the source pptx after the last existing slide. Omit sourceSlideIds
  // to insert every slide; pass a subset to insert only those (source ids
  // from the .pptx's presentation.xml sldId values).
  z.object({
    op: z.literal("insertSlidesFromBase64"),
    base64: z.string().min(1),
    sourceSlideIds: z.array(z.string().min(1)).min(1).optional(),
  }),
  // Replaces the active deck's contents with a snapshot. Inserts the
  // snapshot's slides at the end, then deletes the originally-present slides.
  // Used by the per-message snapshot revert flow.
  z.object({
    op: z.literal("replaceDeckWithBase64"),
    base64: z.string().min(1),
  }),
  // Replaces a single slide in the live deck with one slide from a snapshot.
  // Insert the specified source slide after the current slide's predecessor
  // (or at the start), then delete the original slide at `currentSlideIndex`.
  z.object({
    op: z.literal("replaceSlideFromBase64"),
    base64: z.string().min(1),
    sourceSlideId: z.string().min(1),
    currentSlideIndex: z.number().int().nonnegative(),
  }),
  // Renames inserted slides (by index range) with a scratch title prefix so
  // the cleanup op can identify them. Title-prefix tagging is the v1 marker;
  // hidden custom-XML parts can replace it later if we hit a host that
  // exposes them reliably.
  z.object({
    op: z.literal("tagScratchSlides"),
    firstIndex: z.number().int().nonnegative(),
    count: z.number().int().positive(),
    scratchTag: z.string().min(1),
  }),
  z.object({
    op: z.literal("deleteScratchSlides"),
    scratchTag: z.string().min(1),
  }),
  // Delete slides by their PowerPoint slide IDs. Best-effort — missing IDs
  // are skipped silently. Used by the Compare preview flow, which knows the
  // exact IDs it inserted and doesn't need title-tag matching.
  z.object({
    op: z.literal("deleteSlidesByIds"),
    ids: z.array(z.string().min(1)).min(1),
  }),
  z.object({ op: z.literal("listScratchSlides"), scratchTag: z.string().min(1) }),
  z.object({ op: z.literal("countSlides") }),
  // Returns every shape on a slide with id, name, type, geometry, and (when
  // present) text. The model's most common readback path.
  z.object({ op: z.literal("getSlideContents"), slide: slideRef }),
  // Generic Office.js forwarder: walk a path starting from
  // `context.presentation`, optionally load+sync, return a JSON snapshot.
  // Each segment is either a property name or a method-call descriptor.
  z.object({
    op: z.literal("oj.get"),
    path: z.array(
      z.union([
        z.string(),
        z.object({
          method: z.string(),
          args: z.array(z.unknown()).default([]),
        }),
      ]),
    ),
    // Comma- and slash-separated load string passed to .load() on the final
    // proxy before sync. Omit for ClientResult-typed endpoints.
    load: z.string().optional(),
  }),
  // Generic Office.js forwarder for method calls. Last segment of path must
  // be {method, args}. Returns the call's result.value if it's a
  // ClientResult, or a JSON snapshot of the returned proxy (after sync), or
  // null for void returns.
  z.object({
    op: z.literal("oj.call"),
    path: z.array(
      z.union([
        z.string(),
        z.object({
          method: z.string(),
          args: z.array(z.unknown()).default([]),
        }),
      ]),
    ),
    // Optional load applied to a returned proxy before sync.
    load: z.string().optional(),
  }),
  // Generic Office.js forwarder for property writes. Last segment must be a
  // plain string property name on the second-to-last proxy.
  z.object({
    op: z.literal("oj.set"),
    path: z.array(
      z.union([
        z.string(),
        z.object({
          method: z.string(),
          args: z.array(z.unknown()).default([]),
        }),
      ]),
    ),
    value: z.unknown(),
  }),
  // Returns a rendered PNG of a slide as base64. Used by peek_slide so the
  // model can literally see the slide.
  z.object({
    op: z.literal("getSlideImage"),
    slide: slideRef,
    width: z.number().int().positive().max(4096).optional(),
    height: z.number().int().positive().max(4096).optional(),
  }),
  // Inserts a base64-encoded image as a picture shape on a slide. Used by
  // insert_attachment_image — bytes come from the server which fetched them
  // from OpenAI Files API.
  z.object({
    op: z.literal("addPicture"),
    slide: slideRef,
    base64: z.string().min(1),
    rect: z.object({
      left: z.number(),
      top: z.number(),
      width: z.number().positive(),
      height: z.number().positive(),
    }),
    name: z.string().optional(),
  }),
  // High-throughput bulk shape builder. The model passes a declarative list
  // of shape specs; the bridge creates each shape, sets its properties on
  // the cached proxy (no path re-walk), and syncs in chunks. Prefer this
  // for slide construction — the generic forwarder (oj.get/call/set) burns
  // through PowerPoint's proxy budget and is unreliable at scale.
  z.object({
    op: z.literal("addShapes"),
    slide: slideRef,
    shapes: z
      .array(
        z.discriminatedUnion("kind", [
          z.object({
            kind: z.literal("geometric"),
            geo: z.string().min(1), // GeometricShapeType: "Rectangle" | "Ellipse" | "RoundRectangle" | ...
            rect: z.object({
              left: z.number(),
              top: z.number(),
              width: z.number(),
              height: z.number(),
            }),
            name: z.string().optional(),
            fill: z.string().optional(), // hex colour; omit for no fill change
            line: z
              .object({
                color: z.string().optional(),
                weight: z.number().nonnegative().optional(),
                transparency: z.number().min(0).max(1).optional(),
              })
              .optional(),
          }),
          z.object({
            kind: z.literal("text"),
            text: z.string(),
            rect: z.object({
              left: z.number(),
              top: z.number(),
              width: z.number(),
              height: z.number(),
            }),
            name: z.string().optional(),
            font: z
              .object({
                name: z.string().optional(),
                size: z.number().positive().optional(),
                color: z.string().optional(),
                bold: z.boolean().optional(),
                italic: z.boolean().optional(),
              })
              .optional(),
            paragraph: z
              .object({
                alignment: z.enum(["Left", "Center", "Right", "Justify", "Distributed"]).optional(),
              })
              .optional(),
            textFrame: z
              .object({
                wordWrap: z.boolean().optional(),
                leftMargin: z.number().nonnegative().optional(),
                rightMargin: z.number().nonnegative().optional(),
                topMargin: z.number().nonnegative().optional(),
                bottomMargin: z.number().nonnegative().optional(),
              })
              .optional(),
          }),
        ]),
      )
      .min(1)
      .max(200),
  }),
]);
export type Op = z.infer<typeof OpSchema>;

// Defensive ceiling for raw forwarder use. Bulk slide construction should
// go through the typed `addShapes` op, which handles chunking internally.
export const MAX_OPS_PER_BATCH = 50;
export const MAX_LOG_ENTRIES = 500;
// Keep in sync with the system prompt (~60s) and slightly under
// TOOL_RESULT_TIMEOUT_MS on the server so BudgetExceeded surfaces first.
export const DEFAULT_RUN_TIMEOUT_MS = 60_000;

export const BridgeErrorSchema = z.object({
  name: z.string(),
  message: z.string(),
  stack: z.string().optional(),
  code: z.string().optional(),
  failingOpIndex: z.number().int().nonnegative().optional(),
  op: z.string().optional(),
  hint: z.string().optional(),
  nextStep: z.string().optional(),
});
export type BridgeError = z.infer<typeof BridgeErrorSchema>;

// Messages the sandboxed iframe sends to the parent task pane.
export const IframeToParentMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ready") }),
  z.object({
    type: z.literal("batch"),
    id: z.string().min(1),
    ops: z.array(OpSchema).max(MAX_OPS_PER_BATCH),
  }),
  z.object({ type: z.literal("log"), args: z.array(z.unknown()) }),
  z.object({ type: z.literal("done"), value: z.unknown() }),
  z.object({
    type: z.literal("error"),
    error: BridgeErrorSchema,
  }),
]);
export type IframeToParentMessage = z.infer<typeof IframeToParentMessage>;

export type BridgeResult =
  | { ok: true; log: unknown[][]; opsCount: number; summary: unknown }
  | { ok: false; log: unknown[][]; opsCount: number; error: BridgeError };
